import * as api from "@/api";
import styles from "@/components/api-keys-popup.module.css";
import NewApiKeyPopup from "@/components/new-api-key-popup";
import PopupShell from "@/components/popup-shell";
import globals from "@/globals";
import * as signals from "@/signals";
import { ApiKeyInfo } from "@/types";
import { closePopup, getPremium, showDialog } from "@/utils";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DialogTitle } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { JSX, useState } from "react";

function ApiKeysPopup(): JSX.Element {
	const [isNewApiKeyPopupShown, setIsNewApiKeyPopupShown] = useState(false);
	const [newApiKey, setNewApiKey] = useState("");

	const { data: apiKeys, refetch } = useQuery({
		queryFn: api.getApiKeys,
		queryKey: ["api-keys"],
	});

	const closeApiKeysPopup = (): void => {
		if (isNewApiKeyPopupShown) {
			return;
		}
		signals.isClosingDialog.value = true;
		window.setTimeout(() => {
			setIsNewApiKeyPopupShown(false);
			signals.isClosingDialog.value = false;
			closePopup("apiKeys");
		}, globals.ANIMATION_WAIT_TIME);
	};

	const closeNewApiKeyPopup = (): void => {
		setNewApiKey("");
		setIsNewApiKeyPopupShown(false);
	};

	const createApiKey = async (): Promise<void> => {
		if (!globals.privilege) {
			await showDialog(
				t("premiumRequiredForAutoDeploy") + t("refreshIfJustPaid"),
				{ showCancel: true },
			);
			void getPremium();
			return;
		}
		signals.isLoadingShown.value = true;
		try {
			const data = await api.createApiKey();
			setNewApiKey(data.apiKey);
			await refetch();
			setIsNewApiKeyPopupShown(true);
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	const deleteApiKey = async (id: string): Promise<void> => {
		await showDialog(t("confirmDeleteThisApiKey"), { showCancel: true });
		signals.isLoadingShown.value = true;
		try {
			await api.deleteApiKey(id);
			await refetch();
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	const toggleApiKey = async (apiKeyItem: ApiKeyInfo): Promise<void> => {
		signals.isLoadingShown.value = true;
		try {
			if (apiKeyItem.isDisabled) {
				await api.enableApiKey(apiKeyItem.id);
			} else {
				await api.disableApiKey(apiKeyItem.id);
			}
			await refetch();
		} catch (error) {
			void api.handleApiError(error);
		} finally {
			signals.isLoadingShown.value = false;
		}
	};

	const elements = apiKeys?.results.map((item) => {
		return (
			<tr key={item.id}>
				<td>{item.apiKey}</td>
				<td>{new Date(item.createdAt).toLocaleString()}</td>
				<td>
					{item.lastUsedAt
						? new Date(item.lastUsedAt).toLocaleDateString()
						: "-"}
				</td>
				<td>
					<button
						className="button"
						onClick={() => void toggleApiKey(item)}
						type="button"
					>
						{item.isDisabled ? t("enable") : t("disable")}
					</button>
					<button
						className="button"
						onClick={() => void deleteApiKey(item.id)}
						type="button"
					>
						{t("delete")}
					</button>
				</td>
			</tr>
		);
	});

	return (
		<>
			<PopupShell
				className={styles["container"]}
				isClosing={
					signals.isClosingDialog.value ||
					signals.isLoadingShown.value
				}
				isOpen={!isNewApiKeyPopupShown}
				onClose={closeApiKeysPopup}
			>
				<DialogTitle as="h1">{t("apiKeys")}</DialogTitle>
				<p className={styles["description"]}>
					{t("apiKeyIntroduction")}
					<a
						className="link"
						href="https://docs.retiehe.com/host/auto-deploy.html"
						target="_blank"
						rel="noreferrer"
					>
						{t("viewTutorial")}
					</a>
				</p>
				<button
					className="button"
					onClick={() => void createApiKey()}
					type="button"
				>
					<FontAwesomeIcon icon={faPlus} />
					{t("newKey")}
				</button>
				<div className={styles["table-container"]}>
					<table>
						<thead>
							<tr>
								<th>{t("apiKeys")}</th>
								<th>{t("created")}</th>
								<th>{t("lastUsed")}</th>
								<th>{t("actions")}</th>
							</tr>
						</thead>
						<tbody>
							{apiKeys?.results.length === 0 ? (
								<tr>
									<td>
										{t("youHaveNotCreatedAnyApiKeysYet")}
									</td>
								</tr>
							) : (
								elements
							)}
						</tbody>
					</table>
				</div>
				<div className="button-container">
					<button
						className="default-button"
						onClick={closeApiKeysPopup}
						type="button"
					>
						{t("ok")}
					</button>
				</div>
			</PopupShell>
			<NewApiKeyPopup
				isShown={isNewApiKeyPopupShown}
				newApiKey={newApiKey}
				onClose={closeNewApiKeyPopup}
			/>
		</>
	);
}

export default ApiKeysPopup;
